import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Coin3D from './Coin3D';
import GameResultOverlay from './GameResultOverlay';
import useGameSounds from './useGameSounds';

function Room({ player, opponent, socket, flip, setFlip, changeIsReady, bet, opponentChose, roundReady, currentRoundId, seedRef }) {
    const { t } = useTranslation();
    const [clicked, setClicked] = useState(false);
    const [choice, setChoice] = useState(null);
    const [showResult, setShowResult] = useState(false);
    const [gameResult, setGameResult] = useState(null);
    const [lastRoundId, setLastRoundId] = useState(null);
    const [copied, setCopied] = useState(false);
    const { playWin, playLose, playDraw, playChoose } = useGameSounds();

    const coin = flip && typeof flip === 'object' ? flip.coin : null;
    const winner = flip && typeof flip === 'object' ? flip.winner : null;
    const flipRevealed = coin === 0 || coin === 1;
    const phase = flipRevealed ? 'reveal' : (clicked ? 'spinning' : 'idle');

    // Determine result and trigger effects when flip is revealed
    useEffect(() => {
        if (!flipRevealed) return;
        let result;
        if (winner === 2) {
            result = 'draw';
            playDraw();
        } else if (coin === choice) {
            result = 'win';
            playWin();
            if (navigator.vibrate) navigator.vibrate([100, 50, 100, 50, 200]);
        } else {
            result = 'lose';
            playLose();
            if (navigator.vibrate) navigator.vibrate(150);
        }

        setGameResult(result);
        setShowResult(true);
        setClicked(false);
        if (flip.roundId) setLastRoundId(flip.roundId);

        const timer = setTimeout(() => {
            setShowResult(false);
            setGameResult(null);
            setFlip(null);
            setChoice(null);
            changeIsReady(false);
        }, 3500);
        return () => clearTimeout(timer);
    }, [flip]); // eslint-disable-line

    useEffect(() => {
        if (roundReady) setLastRoundId(null);
    }, [roundReady, currentRoundId]);

    const makeChoose = (value) => {
        if (!roundReady) return;
        const seed = seedRef?.current;
        if (!seed) return;
        setFlip(null);
        setChoice(value);
        setClicked(true);
        playChoose();
        if (navigator.vibrate) navigator.vibrate(30);
        socket.send(JSON.stringify({ eventName: 'make.choose', payload: { choice: value, seed } }));
        changeIsReady(true);
    };

    const copyVerifyLink = async () => {
        if (!lastRoundId) return;
        try {
            await navigator.clipboard.writeText(`${window.location.origin}/verify/${lastRoundId}`);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch {}
    };

    const exitGame = () => {
        socket.send(JSON.stringify({ eventName: 'game.stop', payload: player.name }));
    };

    const opponentStatus = () => {
        if (opponentChose) return (
            <span className="text-green-400 animate-pulse tracking-widest">
                {t('game.ready')} <span className="text-green-300">&#10003;</span>
            </span>
        );
        if (phase === 'spinning') return <span className="text-cyber-cyan animate-pulse tracking-widest">{t('game.choosing')}</span>;
        if (phase === 'reveal') return <span className="text-cyber-purple tracking-widest">{t('game.revealed')}</span>;
        return <span className="text-cyber-dim tracking-widest">{t('game.standby')}</span>;
    };

    return (
        <div className="min-h-screen p-2 sm:p-4 md:p-3 md:h-screen md:max-h-screen md:overflow-hidden">
            <div className="max-w-6xl mx-auto space-y-3 sm:space-y-4 md:space-y-3 md:h-full md:flex md:flex-col">
                {/* Header */}
                <div className="neon-panel scanlines p-3 sm:p-4 flex items-center justify-between md:flex-shrink-0">
                    <div className="flex items-center gap-2 sm:gap-3">
                        <div className="w-2 h-2 sm:w-3 sm:h-3 rounded-full bg-cyber-pink animate-pulse shadow-neon" />
                        <span className="font-display text-xs sm:text-sm tracking-widest text-cyber-pink glow-pink">{t('game.liveMatch')}</span>
                    </div>
                    <div className="flex items-center gap-3 sm:gap-4">
                        <div className="text-center px-3 border-x border-cyber-border">
                            <div className="neon-label text-[10px]">{t('game.bet')}</div>
                            <div className="text-cyber-purple glow-purple font-display font-bold text-sm sm:text-base">{bet}¢</div>
                        </div>
                        <button onClick={exitGame} className="neon-btn neon-btn-pink text-xs">
                            {t('game.abortMatch')}
                        </button>
                    </div>
                </div>

                {/* Mobile: coin */}
                <div className="md:hidden">
                    <div className="neon-panel p-3 relative overflow-hidden">
                        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyber-cyan to-transparent" />
                        <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-cyber-purple to-transparent" />
                        <Coin3D phase={phase} result={coin} />
                        <GameResultOverlay result={gameResult} bet={bet} visible={showResult} />
                        <div className="text-center h-6 font-display tracking-widest text-xs sm:text-sm">
                            {phase === 'idle' && <span className="text-cyber-dim">{t('game.chooseYourSide')}</span>}
                            {phase === 'spinning' && <span className="text-cyber-cyan glow-cyan animate-pulse">{t('game.flipping')}</span>}
                        </div>
                    </div>
                </div>

                {/* Mobile: player panels */}
                <div className="grid grid-cols-2 md:hidden gap-3">
                    <div className="neon-panel p-3 text-center">
                        <div className="neon-label mb-1">{t('game.you')}</div>
                        <div className="text-sm sm:text-lg font-display font-bold text-cyber-cyan glow-cyan tracking-wider truncate">{player.name}</div>
                        <div className="mt-2 neon-label">{t('game.credits')}</div>
                        <div className="text-lg sm:text-2xl font-display font-black text-white">
                            {player.balance}<span className="text-cyber-dim text-xs sm:text-sm ml-1">¢</span>
                        </div>
                        {choice !== null && (
                            <div className="mt-2 text-[10px] text-cyber-dim tracking-widest">
                                <span className={choice === 0 ? 'text-cyber-cyan glow-cyan' : 'text-cyber-purple glow-purple'}>
                                    {choice === 0 ? `☠ ${t('game.skull')}` : `⚡ ${t('game.bolt')}`}
                                </span>
                            </div>
                        )}
                    </div>
                    <div className="neon-panel p-3 text-center">
                        <div className="neon-label mb-1">{t('game.opponent')}</div>
                        <div className="text-sm sm:text-lg font-display font-bold text-cyber-pink glow-pink tracking-wider truncate">
                            {opponent || '...'}
                        </div>
                        <div className="mt-2 neon-label">{t('game.status')}</div>
                        <div className="text-xs mt-1">
                            {opponentStatus()}
                        </div>
                    </div>
                </div>

                {/* Desktop: 3-column layout */}
                <div className="hidden md:grid md:grid-cols-[1fr_1.4fr_1fr] gap-4 items-stretch md:flex-1 md:min-h-0">
                    <div className="neon-panel p-4 flex flex-col items-center justify-center">
                        <div className="neon-label mb-1">{t('game.you')}</div>
                        <div className="text-xl font-display font-bold text-cyber-cyan glow-cyan tracking-wider truncate max-w-full">{player.name}</div>
                        <div className="mt-3 neon-label">{t('game.credits')}</div>
                        <div className="text-2xl font-display font-black text-white">
                            {player.balance}<span className="text-cyber-dim text-sm ml-1">¢</span>
                        </div>
                        {choice !== null && (
                            <div className="mt-3 text-xs text-cyber-dim tracking-widest">
                                {t('game.yourBet')} <span className={choice === 0 ? 'text-cyber-cyan glow-cyan' : 'text-cyber-purple glow-purple'}>
                                    {choice === 0 ? `☠ ${t('game.skull')}` : `⚡ ${t('game.bolt')}`}
                                </span>
                            </div>
                        )}
                    </div>

                    <div className="neon-panel p-3 relative overflow-hidden flex flex-col">
                        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-cyber-cyan to-transparent" />
                        <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-cyber-purple to-transparent" />
                        <div className="flex-1 min-h-0">
                            <Coin3D phase={phase} result={flip} flexible />
                        </div>
                        <GameResultOverlay result={gameResult} bet={bet} visible={showResult} />
                        <div className="text-center h-6 font-display tracking-widest text-sm flex-shrink-0">
                            {phase === 'idle' && <span className="text-cyber-dim">{t('game.chooseYourSide')}</span>}
                            {phase === 'spinning' && <span className="text-cyber-cyan glow-cyan animate-pulse">{t('game.flipping')}</span>}
                        </div>
                    </div>

                    <div className="neon-panel p-4 flex flex-col items-center justify-center">
                        <div className="neon-label mb-1">{t('game.opponent')}</div>
                        <div className="text-xl font-display font-bold text-cyber-pink glow-pink tracking-wider truncate max-w-full">
                            {opponent || '...'}
                        </div>
                        <div className="mt-3 neon-label">{t('game.status')}</div>
                        <div className="text-sm mt-1">
                            {opponentStatus()}
                        </div>
                    </div>
                </div>

                {/* Choice buttons */}
                <div className="neon-panel p-3 sm:p-4 md:flex-shrink-0">
                    <div className="text-center neon-label mb-2 sm:mb-3">{t('game.makeYourChoice')}</div>
                    <div className="grid grid-cols-2 gap-3 sm:gap-4 max-w-xl mx-auto">
                        <button
                            onClick={() => makeChoose(0)}
                            disabled={phase !== 'idle' || !roundReady}
                            className="neon-btn py-4 sm:py-5 md:py-4 text-base sm:text-lg flex flex-col items-center gap-1 sm:gap-2"
                        >
                            <span className="text-2xl sm:text-3xl md:text-2xl">☠</span>
                            <span>{t('game.skull')}</span>
                        </button>
                        <button
                            onClick={() => makeChoose(1)}
                            disabled={phase !== 'idle' || !roundReady}
                            className="neon-btn neon-btn-purple py-4 sm:py-5 md:py-4 text-base sm:text-lg flex flex-col items-center gap-1 sm:gap-2"
                        >
                            <span className="text-2xl sm:text-3xl md:text-2xl">⚡</span>
                            <span>{t('game.bolt')}</span>
                        </button>
                    </div>
                    {phase === 'idle' && !roundReady && (
                        <div className="mt-2 text-center text-[10px] text-cyber-dim tracking-widest animate-pulse">
                            {t('verify.preparingRound')}
                        </div>
                    )}
                    {lastRoundId && (
                        <div className="mt-3 text-center text-[10px] text-cyber-dim tracking-widest flex items-center justify-center gap-2">
                            <span>{t('verify.roundFooter', { id: lastRoundId.substring(0, 8) })}</span>
                            <a
                                href={`/verify/${lastRoundId}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-cyan-400 hover:underline"
                            >
                                {t('verify.verifyLink')}
                            </a>
                            <button
                                onClick={copyVerifyLink}
                                className="text-cyber-dim hover:text-cyan-400 underline"
                            >
                                {copied ? t('verify.copied') : t('verify.copyLink')}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default Room;
